<template>
  <div class="overview-top-posts ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">whatshot</i>
      <Tooltip :text="$t('dash.overview.help.top.title')" position="top">
        <h2 class="ov-help-title">{{ $t('dash.overview.top.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <span v-if="widenedLabel" class="widened-badge">{{ widenedLabel }}</span>
    </div>
    <div v-if="loading" class="top-grid">
      <div v-for="n in 3" :key="n" class="top-skeleton"></div>
    </div>
    <p v-else-if="!topPosts.length" class="ov-empty">{{ $t('dash.overview.top.empty') }}</p>
    <div v-else class="top-grid">
      <div
        v-for="(post, i) in topPosts"
        :key="i"
        class="top-card"
        :class="{ clickable: !!solutionOf(post) || !!post.url }"
        @click="openTop(post)"
      >
        <div class="top-media">
          <img v-if="imageOf(post, i)" :src="imageOf(post, i)" alt="" loading="lazy" referrerpolicy="no-referrer" @error="onImageError(post, i)" />
          <div v-else class="top-media-empty">
            <i class="material-icons">{{ refreshing[i] ? 'hourglass_empty' : 'image' }}</i>
          </div>
          <span class="top-rank">#{{ i + 1 }}</span>
          <div class="top-links" @click.stop>
            <Tooltip v-if="solutionOf(post)" :text="$t('dash.overview.last.open_post')" position="top">
              <button class="ov-icon-btn" @click="openInPosts(post)"><i class="material-icons">open_in_browser</i></button>
            </Tooltip>
            <Tooltip v-if="post.url" :text="$t('dash.overview.last.open_network', { network: $t(`dash.overview.network.${post.network}`) })" position="top">
              <button class="ov-icon-btn" @click="openUrl(post.url)"><i class="material-icons">open_in_new</i></button>
            </Tooltip>
          </div>
        </div>
        <div class="top-body">
          <p class="top-meta">
            <Tooltip v-if="solutionOf(post)" :text="$t('dash.reporting.filter.solution_only')" position="top" class="top-solution">
              <img src="/img/icons/favicon-16x16.png" alt="Mashup Web Social" />
            </Tooltip>
            {{ $t(`dash.overview.network.${post.network}`) }} · {{ post.page_name }}<template v-if="post.date"> · {{ formatDate(post.date) }}</template>
          </p>
          <p class="top-text">{{ post.message || $t('dash.overview.posts.no_text') }}</p>
          <Tooltip :text="$t('dash.overview.help.top.engagement')" position="top" class="top-engagement-wrap">
          <span class="top-engagement">
            <i class="material-icons">favorite_border</i>{{ post.engagement.toLocaleString() }} {{ $t('dash.overview.top.interactions') }}
          </span>
          </Tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Moderation from '../../mixins/Moderation'
import Tooltip from '../tooltip/tooltipInit.vue'

const { VUE_APP_API_HOST } = process.env

// Réseaux dont l'image peut être redemandée via /moderation/post (comme ReportingContent._fetchPageImages)
const REFRESHABLE = ['facebook', 'instagram']

export default {
  name: 'OverviewTopPosts',

  components: { Tooltip },

  mixins: [Moderation],

  props: {
    top: { type: Object, default: null },
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      // index -> URL fraîche, null si introuvable
      freshImages: {},
      refreshing: {},
      // post_id réseau -> news_id Mashup (null si le post n'a pas été publié via Mashup)
      solutions: {}
    }
  },

  computed: {
    topPosts() {
      return (this.top && this.top.posts) || []
    },

    widenedLabel() {
      const range = this.top && this.top.range
      if (!range || !range.widened) return ''
      return range.since
        ? this.$t('dash.overview.widened.since', { date: moment.unix(range.since).format('DD/MM/YYYY') })
        : this.$t('dash.overview.widened.all')
    }
  },

  watch: {
    topPosts: {
      immediate: true,
      handler(posts) {
        this.freshImages = {}
        this.refreshing = {}
        posts.forEach((post, i) => { if (!post.image) this.refreshImage(post, i) })
        this.loadSolutions(posts)
      }
    }
  },

  methods: {
    // Même lookup que le Reporting (ReportingMain : /reporting/solution-post)
    async loadSolutions(posts) {
      await Promise.all(posts.map(async post => {
        const id = String(post.post_id || '')
        if (!id || id in this.solutions) return
        try {
          const res = await this.$http.get(`${VUE_APP_API_HOST}/reporting/solution-post?postId=${encodeURIComponent(id)}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
          const body = (res && res.body) || {}
          this.$set(this.solutions, id, body.is_solution_post && body.news_id ? body.news_id : null)
        } catch (e) {
          this.$set(this.solutions, id, null)
        }
      }))
    },

    solutionOf(post) {
      return this.solutions[String(post.post_id || '')] || null
    },

    // Publié via Mashup : ouverture dans Posts2, sinon sur le réseau
    openTop(post) {
      if (this.solutionOf(post)) this.openInPosts(post)
      else if (post.url) this.openUrl(post.url)
    },

    openInPosts(post) {
      this.$emit('open-post', { news_id: this.solutionOf(post), page_id: post.page_id, bot_id: post.bot_id, date: post.date })
    },

    openUrl(url) {
      window.open(url, '_blank', 'noopener')
    },

    imageOf(post, i) {
      if (i in this.freshImages) return this.freshImages[i]
      return post.image || null
    },

    // URL CDN signée expirée : on redemande l'image au réseau une seule fois
    onImageError(post, i) {
      if (i in this.freshImages) {
        this.$set(this.freshImages, i, null)
        return
      }
      this.refreshImage(post, i)
    },

    async refreshImage(post, i) {
      if (!REFRESHABLE.includes(post.network) || !post.post_id || !post.page_id) {
        this.$set(this.freshImages, i, null)
        return
      }
      this.$set(this.refreshing, i, true)
      let url = null
      try {
        const res = await this.getPostFromRS({ id: post.page_id }, post.post_id, true)
        const data = res && res.body && res.body.post
        if (data) url = post.network === 'facebook' ? data.full_picture : (data.thumbnail_url || data.media_url)
      } catch (e) {
        url = null
      }
      this.$set(this.freshImages, i, url || null)
      this.$set(this.refreshing, i, false)
    },

    formatDate(date) {
      return moment(date).format('DD/MM/YYYY')
    }
  }
}
</script>

<style scoped>
.widened-badge {
  font-size: 12px;
  color: #d96a0c;
  background: #ffe6d0;
  border-radius: 20px;
  padding: 2px 10px;
}
.top-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 700px) {
  .top-grid { grid-template-columns: 1fr; }
}
.top-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #f1edf5;
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s;
}
.top-card.clickable {
  cursor: pointer;
}
.top-card.clickable:hover {
  box-shadow: 0 6px 20px rgba(107, 31, 77, 0.12);
}
.top-links {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
}
.top-links .ov-icon-btn {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}
.top-solution {
  display: inline-flex;
  vertical-align: middle;
  margin-right: 4px;
}
.top-solution img {
  width: 14px;
  height: 14px;
}
.top-media {
  position: relative;
  height: 140px;
  background: #f1edf5;
}
.top-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.top-media-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ab6e93;
}
.top-media-empty .material-icons {
  font-size: 34px;
}
.top-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  background: #6b1f4d;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  padding: 2px 9px;
}
.top-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  padding: 12px;
}
.top-meta {
  font-size: 11px;
  color: #9ba3b7;
}
.top-text {
  flex: 1;
  font-size: 13px;
  color: #121826;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.top-engagement {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  font-size: 12px;
  font-weight: 600;
  color: #6b1f4d;
  background: #f1edf5;
  border-radius: 20px;
  padding: 2px 9px;
}
.top-engagement .material-icons {
  font-size: 14px;
}
.top-skeleton {
  height: 240px;
  border-radius: 10px;
  background: #f1edf5;
}
</style>
